// Ambient generative backdrop for the hero. This is a deliberate nod to
// Abhilash's own "3D Terrain from Music" side project: layered sine waves
// standing in for spectral bands, drifting slowly. No audio is captured —
// it's a static seed animated over time, purely decorative, and paused
// automatically when off-screen or backgrounded to be kind to battery/CPU.
export function initHeroTerrain() {
  const canvas = document.getElementById("hero-terrain");
  if (!canvas || !canvas.getContext) return;

  const ctx = canvas.getContext("2d");
  let width, height, dpr;
  let rafId = null;
  let running = false;
  let t = 0;

  const bands = [
    { amp: 22, freq: 0.006, speed: 0.0035, colorVar: "--color-accent-coral", offset: 0 },
    { amp: 34, freq: 0.004, speed: 0.002, colorVar: "--color-accent-teal", offset: 60 },
    { amp: 16, freq: 0.009, speed: 0.0045, colorVar: "--color-accent-violet", offset: 120 },
    { amp: 26, freq: 0.005, speed: 0.0025, colorVar: "--color-accent-amber", offset: 180 },
  ];

  function resize() {
    const rect = canvas.parentElement.getBoundingClientRect();
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    width = rect.width;
    height = rect.height;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = width + "px";
    canvas.style.height = height + "px";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function colorFor(varName) {
    return getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || "#ff6b4a";
  }

  function draw() {
    ctx.clearRect(0, 0, width, height);

    bands.forEach((band, i) => {
      const baseline = height * (0.32 + i * 0.16) + band.offset * 0;
      ctx.beginPath();
      ctx.moveTo(0, baseline);
      for (let x = 0; x <= width; x += 6) {
        const y =
          baseline +
          Math.sin(x * band.freq + t * band.speed * 60 + band.offset) * band.amp +
          Math.sin(x * band.freq * 2.3 + t * band.speed * 40) * (band.amp * 0.3);
        ctx.lineTo(x, y);
      }
      ctx.strokeStyle = colorFor(band.colorVar);
      ctx.globalAlpha = 0.35 - i * 0.04;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    });

    t += 1;
  }

  function loop() {
    if (!running) return;
    draw();
    rafId = requestAnimationFrame(loop);
  }

  function start() {
    if (running) return;
    running = true;
    rafId = requestAnimationFrame(loop);
  }

  function stop() {
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
  }

  resize();
  start();

  window.addEventListener("resize", resize);

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stop();
    else start();
  });

  // Pause when scrolled out of view.
  const observer = new IntersectionObserver(
    (entries) => entries.forEach((entry) => (entry.isIntersecting ? start() : stop())),
    { threshold: 0 }
  );
  observer.observe(canvas);
}
